import {
    useStyles,
    VideoPlayer,
    Flex,
    Header,
    Caption,
    Button,
    Image,
    Dialog,
} from '@archipro-design/aria';
import * as S from './DirectoryHeroPotm.style';
import { useInViewport, useScroll, useSize } from 'ahooks';
import { Link } from 'react-router';
import { useRef, useState, useEffect } from 'react';
import { CloseIcon } from '@fluentui/react-icons-northstar';
import { useIsWebmLegacyDevice } from '@modules/root/hook/use-is-webm-legacy-device';
import { useHydrated } from 'remix-utils';
import { FilePlayer } from '../lazy-file-player';
import { useLogImpression, useTracker } from '@archipro-website/tracker';

import { ChevronDown } from '@archipro-design/icons';

export interface DirectoryHeroPotmProps {
    id: string;
    title: string;
    professionalID: string;
    shortDescription?: string;
    ctaText: string;
    ctaLink?: string;
    projectLink?: string;
    autoplay: boolean;
    teaserVideoWebm: string;
    teaserVideoMp4: string;
    mobileVideoWebm: string;
    mobileVideoMp4: string;
    videoCoverDesktop: string;
    videoCoverMobile?: string;
    fullVideoLink?: string;
    projectsCount?: number;
    projectsRef?: React.RefObject<HTMLDivElement | null>;
}

const DirectoryHeroPotmDesktop = ({
    id,
    title,
    professionalID,
    shortDescription,
    ctaText,
    ctaLink,
    projectLink,
    autoplay,
    videoCoverDesktop,
    teaserVideoWebm,
    teaserVideoMp4,
    fullVideoLink,
    projectsCount,
    projectsRef,
}: DirectoryHeroPotmProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const IS_CLIENT_SIDE = typeof window !== 'undefined';

    const [playing, setPlaying] = useState(false);
    const [videoSources, setVideoSources] = useState([
        teaserVideoWebm,
        teaserVideoMp4,
    ]);
    const hydrated = useHydrated();
    const position = useScroll();
    const heroSize = useSize(ref);
    const screenSize = {
        width: IS_CLIENT_SIDE ? window.innerWidth : 0,
        height: IS_CLIENT_SIDE ? window.innerHeight : 0,
    };

    const { keyframe } = useStyles();
    const { css } = useStyles({
        autoplay,
        playing,
        scrollY: position?.top,
        heroSize,
        screenSize,
        upDownMotionKeyframes: keyframe(S.UpDownMotionKeyframes),
    });
    const [inViewport] = useInViewport(ref, {
        threshold: 0.2,
    });

    const tracker = useTracker();
    useLogImpression({
        data: {
            professionalID: Number(professionalID),
            itemID: Number(id),
            type: 'Potm_Directory',
        },
        ref,
    });

    const isWebmLegacyDevice = useIsWebmLegacyDevice();
    const [openFullVideo, setOpenFullVideo] = useState(false);

    const scrollToProjectsCount = projectsCount
        ? Math.floor(projectsCount / 1000) * 1000
        : 0;

    useEffect(() => {
        setVideoSources(
            isWebmLegacyDevice
                ? [teaserVideoMp4, teaserVideoWebm]
                : [teaserVideoWebm, teaserVideoMp4],
        );
    }, [teaserVideoMp4, teaserVideoWebm, isWebmLegacyDevice]);

    //server render only cover image
    const video = hydrated ? (
        <div className={css(S.Player)}>
            <FilePlayer
                url={videoSources}
                playing={autoplay && inViewport}
                muted={true}
                loop={true}
                controls={false}
                playsinline={true}
                width={'100%'}
                height={'100%'}
                onPlay={() => setPlaying(true)}
                config={{
                    attributes: {
                        preload: 'auto',
                        poster: videoCoverDesktop,
                    },
                }}
            />
        </div>
    ) : null;

    const projectModalSearchParams = projectLink
        ? new URLSearchParams(decodeURI(projectLink).split('?')[1])
        : undefined;

    if (projectModalSearchParams) {
        projectModalSearchParams.set('modal-video', '1');
    }

    const projectModalLink =
        projectLink && projectModalSearchParams
            ? `${projectLink}?${projectModalSearchParams}`
            : undefined;

    const trackClick = () => {
        tracker.log('PotmHeroClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: JSON.stringify({
                    libraryItemId: id,
                }),
            },
            ga4Data: {
                libraryItemId: id,
            },
        });
    };

    const trackPotmVideoClick = () => {
        tracker.log('PotmHeroVideoClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: JSON.stringify({
                    videoLink: fullVideoLink,
                    libraryItemId: id,
                }),
            },
            ga4Data: {
                libraryItemId: id,
            },
        });
    };

    const watchNowButton = projectModalLink ? (
        <Button
            as={Link}
            to={projectModalLink}
            className={css(S.VideoButton)}
            color={'white'}
            size={14}
            onMouseDown={trackPotmVideoClick}
        >
            Watch now
        </Button>
    ) : (
        fullVideoLink && (
            <Button
                className={css(S.VideoButton)}
                color={'white'}
                size={14}
                onMouseDown={trackPotmVideoClick}
                onClick={() => {
                    setOpenFullVideo(true);
                }}
            >
                Watch now
            </Button>
        )
    );

    const scrollToProjects = () => {
        if (projectsRef?.current) {
            projectsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div ref={ref} className={css(S.DirectoryHeroContainer)}>
            <div className={css(S.Frame)}>
                <div className={css(S.VideoAspect)}>
                    {video}
                    <Image
                        className={css(S.Cover)}
                        src={videoCoverDesktop}
                        height={1080}
                        width={1920}
                        layout={'intrinsic'}
                        objectFit={'cover'}
                        objectPosition={'center'}
                        alt={title || ''}
                    />
                    <div className={css(S.DirectoryHeroOverlay)}>
                        <Flex
                            vAlign={'center'}
                            fill={true}
                            className={css(S.Spacer)}
                        >
                            <div className={css(S.DirectoryHeroContent)}>
                                <Header as={'h2'} className={css(S.title)}>
                                    {title}
                                </Header>
                                {shortDescription && (
                                    <Caption
                                        variant={'01'}
                                        className={css(S.Description)}
                                    >
                                        {shortDescription}
                                    </Caption>
                                )}
                                <div className={css(S.Actions)}>
                                    {watchNowButton}
                                    {ctaLink && (
                                        <Button
                                            as={Link}
                                            to={ctaLink}
                                            variant={'outlined'}
                                            color={'primary'}
                                            className={css(S.PrimaryButton)}
                                            size={14}
                                            onClick={trackClick}
                                        >
                                            {ctaText}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Flex>

                        <div
                            className={css(S.DirectoryHeroViewMore)}
                            onClick={scrollToProjects}
                        >
                            <span className={css(S.DirectoryHeroViewMoreLabel)}>
                                Scroll to discover
                                {scrollToProjectsCount > 0
                                    ? ` over ${scrollToProjectsCount
                                          .toString()
                                          .replace(
                                              /\B(?=(\d{3})+(?!\d))/g,
                                              ',',
                                          )} Projects`
                                    : ' more Projects'}
                            </span>
                            <div className={css(S.DirectoryHeroViewMoreIcon)}>
                                <ChevronDown />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {fullVideoLink && (
                <Dialog
                    className={css(S.FullVideoModal)}
                    styles={{ maxWidth: '100vw', maxHeight: '100vh' }}
                    variables={{
                        overlayBackground: 'rgba(30,30,30,.9)',
                        rootBackground: 'transparent',
                        rootWidth: '100%',
                        rootHeight: '100%',
                        border: '0',
                        contentTopBorderColor: 'transparent',
                        rootPadding: '0',
                        overflow: false,
                    }}
                    open={openFullVideo}
                    onCancel={() => setOpenFullVideo(false)}
                    backdrop={true}
                    headerAction={{
                        icon: (
                            <CloseIcon className={css(S.FullVideoModalClose)} />
                        ),
                        title: 'Close',
                        onClick: () => setOpenFullVideo(false),
                    }}
                    onClick={() => setOpenFullVideo(false)}
                    content={
                        <div className={css(S.VideoAspect)}>
                            <VideoPlayer
                                src={fullVideoLink}
                                playing={true}
                                controls={true}
                                playsInline={true}
                                trackPlayEvent={trackPotmVideoClick}
                            />
                        </div>
                    }
                />
            )}
        </div>
    );
};

export default DirectoryHeroPotmDesktop;
